import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { createWsClient, type WsClient, type WsClientFrame } from '@neon-one/auctions-types';

import { env } from '@/shell/env';
import { useSession } from '@/shell/stores/session';

type ConnectionState = 'idle' | 'connecting' | 'connected' | 'disconnected';
type FrameHandler = (frame: WsClientFrame) => void;

export const useWs = defineStore('ws', () => {
  const state = ref<ConnectionState>('idle');
  let client: WsClient | null = null;
  // Track channels per handler so a forced reconnect can replay subscriptions
  // on the new client instance (createWsClient's internal channelSet is lost
  // when the client is closed).
  const handlers = new Map<FrameHandler, { sub: ReturnType<WsClient['on']>; channels: Set<string> }>();

  const isConnected = computed(() => state.value === 'connected');
  const isDisconnected = computed(() => state.value === 'disconnected');

  function ensureClient(): WsClient {
    if (client) return client;
    client = createWsClient({
      baseUrl: env.wsBaseUrl,
      getJwt: () => useSession().getJwt(),
      // Keep the pinia state ref in sync with the underlying socket so
      // useReconnectRefresh fires on auto-reconnect (AC6). Without this, a
      // drop + auto-reconnect inside createWsClient is invisible to the
      // store and the UI never refetches stale state.
      onStatusChange: status => {
        if (status === 'connecting') state.value = 'connecting';
        else if (status === 'open') state.value = 'connected';
        else if (status === 'closed' || status === 'error') state.value = 'disconnected';
      },
    });
    return client;
  }

  async function connect(): Promise<void> {
    const c = ensureClient();
    try {
      await c.connect();
    } catch {
      // onStatusChange has already moved state to 'disconnected' / 'error'.
    }
  }

  function subscribe(channels: string[], handler: FrameHandler): void {
    const c = ensureClient();
    let entry = handlers.get(handler);
    if (!entry) {
      // createWsClient.dispatch() delivers every received frame to every
      // registered c.on() handler regardless of channel. Wrap the handler
      // so it only fires for frames arriving on channels this subscription
      // actually requested. Without this guard, bid.applyFrame receives
      // bid.max_exceeded from the bidder:{uuid} channel (subscribed by
      // App.vue) and switches the bid type to 'max' even though the handler
      // was only registered for item:{id} frames.
      const channelSet = new Set<string>();
      const filtered: FrameHandler = (frame) => {
        const ch = frame.channel;
        if (!ch || channelSet.has(ch)) handler(frame);
      };
      entry = { sub: c.on(filtered), channels: channelSet };
      handlers.set(handler, entry);
    }
    for (const ch of channels) entry.channels.add(ch);
    c.subscribe(channels);
  }

  function unsubscribe(channels: string[], handler: FrameHandler): void {
    const entry = handlers.get(handler);
    if (entry) {
      for (const ch of channels) entry.channels.delete(ch);
      // Only detach the handler entirely when it has no remaining channels.
      // Calling unsubscribe() unconditionally would drop live updates for any
      // channels the handler is still subscribed to.
      if (entry.channels.size === 0) {
        entry.sub.unsubscribe();
        handlers.delete(handler);
      }
    }
    // Only drop channels from the shared WsClient channelSet (and send the
    // server-side unsubscribe op) for channels no remaining handler still
    // needs. Without this guard, unmounting component A removes event:{uuid}
    // from DynamoDB even though component B (e.g. MyBidsPage) is still
    // subscribed to it — because createWsClient.channelSet is shared.
    const stillNeeded = new Set<string>();
    for (const [, e] of handlers) {
      for (const ch of e.channels) stillNeeded.add(ch);
    }
    const toRemove = channels.filter(ch => !stillNeeded.has(ch));
    if (toRemove.length > 0) {
      client?.unsubscribe(toRemove);
    }
  }

  // Remove all subscriptions for channels whose name starts with `prefix`
  // across every registered handler. Used before a sign-out reconnect so
  // bidder:* channels owned by any page (MyBidsPage, WinsPage, etc.) are
  // not replayed on the anonymous socket.
  function unsubscribePrefix(prefix: string): void {
    const removed = new Set<string>();
    for (const [handler, entry] of handlers) {
      const matched: string[] = [];
      for (const ch of entry.channels) {
        if (ch.startsWith(prefix)) matched.push(ch);
      }
      for (const ch of matched) {
        entry.channels.delete(ch);
        removed.add(ch);
      }
      if (entry.channels.size === 0) {
        entry.sub.unsubscribe();
        handlers.delete(handler);
      }
    }
    if (removed.size > 0) {
      client?.unsubscribe(Array.from(removed));
    }
  }

  function disconnect(): void {
    client?.close();
    client = null;
    handlers.clear();
    state.value = 'idle';
  }

  // Force a fresh socket. Mobile NAT timeout or iOS Safari tab suspend can
  // leave the client believing the socket is open while AWS's channel-index
  // still points at a stale connection_id that no longer receives frames.
  // Page-driven (e.g., document visibilitychange).
  async function reconnect(): Promise<void> {
    if (!client) {
      return connect();
    }
    // Capture handlers + their channels before destroying the client.
    const snapshot = Array.from(handlers.entries()).map(([h, entry]) => ({
      handler: h,
      channels: Array.from(entry.channels),
    }));
    client.close();
    client = null;
    handlers.clear();
    state.value = 'idle';
    await connect();
    // Replay subscriptions on the new client. createWsClient's internal
    // flushSubscribe will send the channels server-side once the socket opens.
    for (const { handler, channels } of snapshot) {
      subscribe(channels, handler);
    }
  }

  return { state, isConnected, isDisconnected, connect, subscribe, unsubscribe, unsubscribePrefix, disconnect, reconnect };
});
